import { DeleteTwoTone } from '@ant-design/icons'
import { themes } from '@theme'
import i18next, { t } from 'i18next'
import { Controller, useFormContext } from 'react-hook-form'
import { Button, HookFormTextArea, Input } from 'src/common'
import styled from 'styled-components'
import { TExperienceForm } from '../types'
import { enumBreakPoint, FORMAT_FR_DATE, FORMAT_ISO_DATE } from '@configs'
import { Checkbox, Typography } from 'antd'
import { toast } from 'react-toastify'
import { formatDate } from '@utils'

export const ExperienceForm = ({
  data,
  index,
  onRemove,
  onSave,
}: TExperienceForm) => {
  const { control, trigger, setValue, getValues, watch } = useFormContext()

  const isCurrentJob = watch(
    `specificInformation.workExperience[${index}].isPresent`
  )

  const handleSaveForm = async () => {
    const isValid = await trigger([
      `specificInformation.workExperience[${index}].companyName`,
      `specificInformation.workExperience[${index}].jobTitle`,
      `specificInformation.workExperience[${index}].description`,
      `specificInformation.workExperience[${index}].startDate`,
      `specificInformation.workExperience[${index}].endDate`,
    ])

    if (isValid) {
      toast.success(t('signUp:saveProfessionalInformation'))
      setValue(`specificInformation.workExperience[${index}].editable`, false)
      onSave?.(index)
    } else {
      toast.error(t('signUp:errorRequireFields'))
    }
  }

  return (
    <StyledExperienceForm className="reset-password__section">
      {!!onRemove && (
        <DeleteTwoTone
          onClick={() => onRemove?.(index)}
          className="self-end text-base"
          twoToneColor={themes.theme.light.colors.icon_gray_500}
        />
      )}

      <Controller
        name={`specificInformation.workExperience[${index}].companyName`}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <Input
              label={i18next.t('common:companyName')}
              alignment="col"
              placeholder={i18next.t('common:enterCompanyName')}
              name={`specificInformation.workExperience[${index}].companyName`}
              containerClassName={'mt-5'}
              errors={error?.message}
              value={value}
              onChange={onChange}
              required
            />
          )
        }}
      />
      <Controller
        name={`specificInformation.workExperience[${index}].jobTitle`}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <Input
              label={i18next.t('common:jobTitle')}
              alignment="col"
              placeholder={i18next.t('common:enterJobTitle')}
              name={`specificInformation.workExperience[${index}].jobTitle`}
              containerClassName={'mt-5'}
              errors={error?.message}
              value={value}
              onChange={onChange}
              allowClear={false}
              required
            />
          )
        }}
      />
      <div className="col-span-8 mt-5">
        <HookFormTextArea
          control={control}
          controlName={`specificInformation.workExperience[${index}].description`}
          label={i18next.t('common:description')}
          labelClassName="mb-2"
          rows={5}
          placeholder={i18next.t('common:description')}
          required
        />
      </div>
      <div className="flex flex-row w-full mt-5 gap-10 items-start">
        <Controller
          name={`specificInformation.workExperience[${index}].startDate`}
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <Input
                label={i18next.t('common:startDate')}
                alignment="col"
                type="date"
                haveShowPassIcon
                dateFormat={FORMAT_FR_DATE}
                name={`specificInformation.workExperience[${index}].startDate`}
                value={value}
                onChangeDate={(value) => {
                  const formattedValue = formatDate(
                    value,
                    FORMAT_ISO_DATE,
                    FORMAT_FR_DATE
                  )
                  onChange(formattedValue)
                  const currentEndDate = getValues(
                    `specificInformation.workExperience[${index}].endDate`
                  )
                  if (!!currentEndDate) {
                    trigger(
                      `specificInformation.workExperience[${index}].endDate`
                    )
                  }
                }}
                errors={error?.message}
                inputReadOnly
                allowClear={false}
                required
              />
            )
          }}
        />
        <Controller
          name={`specificInformation.workExperience[${index}].endDate`}
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <Input
                label={i18next.t('common:endDate')}
                alignment="col"
                type="date"
                haveShowPassIcon
                dateFormat={FORMAT_FR_DATE}
                name={`specificInformation.workExperience[${index}].endDate`}
                value={value}
                onChangeDate={(value) => {
                  const formattedValue = formatDate(
                    value,
                    FORMAT_ISO_DATE,
                    FORMAT_FR_DATE
                  )
                  onChange(formattedValue)
                  const currentStartDate = getValues(
                    `specificInformation.workExperience[${index}].startDate`
                  )
                  if (!!currentStartDate) {
                    trigger(
                      `specificInformation.workExperience[${index}].startDate`
                    )
                  }
                }}
                errors={isCurrentJob ? '' : error?.message}
                inputReadOnly
                allowClear={false}
                required={!isCurrentJob}
                disabled={isCurrentJob}
              />
            )
          }}
        />

        <Controller
          name={`specificInformation.workExperience[${index}].isPresent`}
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <Checkbox
                checked={value}
                onChange={(e) => {
                  onChange(e.target.checked)
                  setValue(
                    `specificInformation.workExperience[${index}].endDate`,
                    ''
                  )
                }}
                style={{
                  transform: 'translateY(50%)',
                  marginTop: 16,
                }}
              >
                <Typography style={{ textWrap: 'nowrap' }}>
                  Current job
                </Typography>
              </Checkbox>
            )
          }}
        />
      </div>
      <div className="flex items-center max-[800px] justify-center my-5">
        <Button
          htmlType="submit"
          type="primary"
          size="small"
          className="min-[600px] max-[600px]:!w-full"
          onClick={handleSaveForm}
        >
          {i18next.t('common:save')}
        </Button>
      </div>
    </StyledExperienceForm>
  )
}

export const StyledExperienceForm = styled.section`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
  .heading {
    margin-bottom: 3.6rem;
    .heading__title {
      text-align: center;
      font-size: 3.2rem;
      font-weight: 700;
      margin-bottom: 0.8rem;
      color: ${(p) => themes.theme.light.colors.text};
    }
    .desc {
      font-size: 1.6rem;
      font-weight: 400;
      color: ${(p) => themes.theme.light.colors.subText};
    }
  }

  .actions {
    @media (min-width: ${enumBreakPoint.DESKTOP}) {
      display: flex;
      align-items: center;
      .btn {
        width: 12.8rem;
      }
    }
  }

  .reset-form {
    width: 100%;
    display: flex;
    flex-direction: column;
    /* align-items: center; */
    .shared-input {
      margin-bottom: 2rem;
      .inner-input {
        height: 4.6rem;
      }
    }
    .submit__btn {
      @media (min-width: ${enumBreakPoint.DESKTOP}) {
        margin-right: 1.2rem;
        margin-bottom: 0;
      }
      margin-bottom: 1.2rem;
    }

    .inner-input {
      width: 100%;
      height: 4.6rem;
    }

    .agree-policy__cb {
      margin-bottom: 1.8rem;
    }
  }
`
